import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Paper,
  Stack
} from '@mui/material';

// 遊戲配置常數
const ROWS = 16;
const COLS = 56;
const QUESTIONS = 10;
const ANSWER_SIZE = 3; // 3x3 的正方形區域
const COLORS = ['#4CAF50', '#FFC107', '#F44336', '#2196F3']; // 綠黃紅藍

export default function Game() {
  // 遊戲狀態
  const [board, setBoard] = useState([]);
  const [score, setScore] = useState(0);
  const [currentQuestion, setCurrentQuestion] = useState(1);
  const [isPlaying, setIsPlaying] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [answerPosition, setAnswerPosition] = useState({ topLeft: { row: 0, col: 0 } });
  const [showAnswer, setShowAnswer] = useState(false);
  const [message, setMessage] = useState('');
  const [questionPattern, setQuestionPattern] = useState([]);
  const [questionStartTime, setQuestionStartTime] = useState(null);
  const [timeLeft, setTimeLeft] = useState(3);

// 修改 calculateCellSize 函數（大約在第 33-45 行）：
const calculateCellSize = () => {
  // 獲取可用空間（減去題目區域和控制區域）
  const availableWidth = window.innerWidth - 320; // 題目區域寬度 300px + 一些間距
  const availableHeight = window.innerHeight - 200; // 減去頂部和底部區域
  
  // 根據可用空間和燈板尺寸計算最大可能的大小
  const maxCellWidth = Math.floor(availableWidth / COLS);
  const maxCellHeight = Math.floor(availableHeight / ROWS);
  
  // 取兩者中較小的值，但要確保最小值
  const cellSize = Math.max(10, Math.min(maxCellWidth, maxCellHeight));
  
  return cellSize;
};

  const [cellSize, setCellSize] = useState(calculateCellSize());

  // 監聽窗口大小變化
  useEffect(() => {
    const handleResize = () => {
      setCellSize(calculateCellSize());
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 計時器
useEffect(() => {
  if (!isPlaying || showAnswer || gameOver) return;

  const timer = setInterval(() => {
    setTimeLeft(prev => prev + 1);
  }, 1000);

  return () => clearInterval(timer);
}, [isPlaying, showAnswer, gameOver]);



  // 初始化遊戲板
  const initializeBoard = useCallback(() => {
    const newBoard = [];
    for (let i = 0; i < ROWS; i++) {
      const row = [];
      for (let j = 0; j < COLS; j++) {
        const colorIndex = Math.floor(Math.random() * COLORS.length);
        row.push({
          id: `${i}-${j}`,
          color: COLORS[colorIndex],
          isPartOfAnswer: false,
          isClicked: false,
        });
      }
      newBoard.push(row);
    }
    return newBoard;
  }, []);

  // 生成題目模式 (3x3區域)
  const generateQuestionPattern = useCallback(() => {
    const pattern = [];
    for (let i = 0; i < ANSWER_SIZE; i++) {
      const row = [];
      for (let j = 0; j < ANSWER_SIZE; j++) {
        const colorIndex = Math.floor(Math.random() * COLORS.length);
        row.push({
          color: COLORS[colorIndex],
        });
      }
      pattern.push(row);
    }
    return pattern;
  }, []);

  // 生成答案位置 (3x3區域)
  const generateAnswerPosition = useCallback(() => {
    const maxRow = ROWS - ANSWER_SIZE;
    const maxCol = COLS - ANSWER_SIZE;
    
    const row = Math.floor(Math.random() * maxRow);
    const col = Math.floor(Math.random() * maxCol);
    
    return { topLeft: { row, col } };
  }, []);

  // 開始新遊戲
  const startNewGame = () => {
    const newBoard = initializeBoard();
    const questionPattern = generateQuestionPattern();
    const answerPos = generateAnswerPosition();
    
    // 將題目模式應用到答案區域
    for (let i = 0; i < ANSWER_SIZE; i++) {
      for (let j = 0; j < ANSWER_SIZE; j++) {
        const row = answerPos.topLeft.row + i;
        const col = answerPos.topLeft.col + j;
        newBoard[row][col].color = questionPattern[i][j].color;
        newBoard[row][col].isPartOfAnswer = true;
      }
    }
    
    setBoard(newBoard);
    setAnswerPosition(answerPos);
    setQuestionPattern(questionPattern);
    setScore(0);
    setCurrentQuestion(1);
    setIsPlaying(true);
    setGameOver(false);
    setShowAnswer(false);
    setTimeLeft(3);
    setMessage(`第 1 題：找出與題目相同的 3×3 區域！`);
    setQuestionStartTime(Date.now());
  };

  // 開始下一題
  const nextQuestion = () => {
    if (currentQuestion >= QUESTIONS) {
      setGameOver(true);
      return;
    }

    const newBoard = initializeBoard();
    const questionPattern = generateQuestionPattern();
    const answerPos = generateAnswerPosition();
    
    // 將題目模式應用到答案區域
    for (let i = 0; i < ANSWER_SIZE; i++) {
      for (let j = 0; j < ANSWER_SIZE; j++) {
        const row = answerPos.topLeft.row + i;
        const col = answerPos.topLeft.col + j;
        newBoard[row][col].color = questionPattern[i][j].color;
        newBoard[row][col].isPartOfAnswer = true;
      }
    }
    
    setBoard(newBoard);
    setAnswerPosition(answerPos);
    setQuestionPattern(questionPattern);
    setCurrentQuestion(prev => prev + 1);
    setShowAnswer(false);
    setTimeLeft(3);
    setMessage(`第 ${currentQuestion + 1} 題：找出與題目相同的 3×3 區域！`);
    setQuestionStartTime(Date.now());
  };

  // 處理燈板點擊
  const handleCellClick = (row, col) => {
    if (!isPlaying || showAnswer || gameOver) return;

    const updatedBoard = board.map(r => r.map(cell => ({ ...cell })));
    updatedBoard[row][col].isClicked = true;
    setBoard(updatedBoard);

    const isCorrect = updatedBoard[row][col].isPartOfAnswer;
    
    if (isCorrect) {
      setScore(prev => prev + 1);
      setMessage('✅ 正確！點中了 3×3 區域！');
    } else {
      setMessage('❌ 錯誤！沒有點中 3×3 區域。');
    }
    
    setShowAnswer(true);
  };

  // 處理遊戲結束
  const handleGameOverClose = () => {
    setGameOver(false);
    setIsPlaying(false);
    setMessage('遊戲結束！點擊「開始遊戲」重新開始。');
  };

  // 初始化遊戲
  useEffect(() => {
    if (!isPlaying) {
      const initialBoard = initializeBoard();
      setBoard(initialBoard);
      setMessage('準備開始遊戲！點擊「開始遊戲」按鈕開始。');
    }
  }, [initializeBoard, isPlaying]);

  // 渲染單個燈板單元格
  const renderCell = (cell, rowIndex, colIndex) => {
    const isInAnswerArea = showAnswer && cell.isPartOfAnswer;
    const isTopLeft = showAnswer && 
      rowIndex === answerPosition.topLeft.row && 
      colIndex === answerPosition.topLeft.col;
    
    return (
      <Box
        key={cell.id}
        sx={{
          width: `${cellSize}px`,
          height: `${cellSize}px`,
          backgroundColor: cell.color,
          border: '1px solid rgba(0, 0, 0, 0.3)',
          borderRadius: '50%',
          cursor: isPlaying && !showAnswer ? 'pointer' : 'default',
          transition: 'all 0.2s',
          position: 'relative',
          boxShadow: 'inset 0 0 5px rgba(0, 0, 0, 0.3), 0 2px 4px rgba(0, 0, 0, 0.2)',
          '&:hover': isPlaying && !showAnswer ? {
            transform: 'scale(1.15)',
            zIndex: 1,
            boxShadow: 'inset 0 0 5px rgba(0, 0, 0, 0.3), 0 0 15px rgba(255, 255, 255, 0.8)',
          } : {},
          ...(isInAnswerArea && {
            boxShadow: 'inset 0 0 0 3px white, 0 0 10px rgba(255, 255, 255, 0.7)',
          }),
          ...(cell.isClicked && {
            opacity: 0.9,
          }),
        }}
        onClick={() => handleCellClick(rowIndex, colIndex)}
      >
        {isTopLeft && showAnswer && (
          <Box
            sx={{
              position: 'absolute',
              top: `-${cellSize * 0.3}px`,
              left: `-${cellSize * 0.3}px`,
              width: `${cellSize * 1.6}px`,
              height: `${cellSize * 1.6}px`,
              border: '3px solid white',
              borderRadius: '8px',
              zIndex: 2,
              pointerEvents: 'none',
              boxShadow: '0 0 15px rgba(255, 255, 255, 0.5)',
            }}
          />
        )}
      </Box>
    );
  };

  // 渲染題目區域
  const renderQuestionArea = () => {
    const questionCellSize = 50;

    return (
      <Paper
        elevation={6}
        sx={{
          width: 300,
          height: '100%',
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          color: 'white',
          p: 3,
          borderRadius: 2,
          border: '2px solid rgba(255, 255, 255, 0.2)',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 'bold', textAlign: 'center', color: '#FFC107' }}>
          第 {currentQuestion} 題
        </Typography>
        
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="h6" sx={{ mb: 2, textAlign: 'center' }}>
            找出相同的 3×3 模式
          </Typography>
          
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: `repeat(3, ${questionCellSize}px)`,
              gridTemplateRows: `repeat(3, ${questionCellSize}px)`,
              gap: '8px',
              justifyContent: 'center',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              p: 2,
              borderRadius: 2,
              border: '2px solid rgba(255, 255, 255, 0.2)',
            }}
          >
            {questionPattern.map((row, rowIndex) =>
              row.map((cell, colIndex) => (
                <Box
                  key={`q-${rowIndex}-${colIndex}`}
                  sx={{
                    width: `${questionCellSize}px`,
                    height: `${questionCellSize}px`,
                    backgroundColor: cell.color,
                    borderRadius: '50%',
                    border: '3px solid white',
                    boxShadow: '0 0 10px rgba(255, 255, 255, 0.3)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                />
              ))
            )}
          </Box>

          <Typography variant="body2" sx={{ mt: 3, textAlign: 'center', color: 'rgba(255, 255, 255, 0.8)' }}>
            在 56×16 的燈板中找出相同的 3×3 區域
          </Typography>
          
          <Typography variant="body2" sx={{ mt: 1, textAlign: 'center', color: '#4CAF50' }}>
            點擊 3×3 區域內的任何一個燈即可得分
          </Typography>
        </Box>

        {/* 計時器 */}
        <Box sx={{ mt: 'auto', textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
            本題用時
          </Typography>
          <Typography variant="h4" sx={{ color: timeLeft > 10 ? '#4CAF50' : '#FFC107' }}>
            {timeLeft} 秒
          </Typography>
        </Box>
      </Paper>
    );
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        backgroundColor: 'rgba(0, 0, 0, 0.95)',
        color: 'white',
        overflow: 'hidden',
      }}
    >
      {/* 頂部狀態欄 */}
      <Box sx={{ 
        backgroundColor: 'rgba(0, 0, 0, 0.8)', 
        p: 2, 
        borderBottom: '2px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold', color: '#FFC107' }}>
          3×3 燈板記憶遊戲
        </Typography>
        
        <Box sx={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              題目
            </Typography>
            <Typography variant="h6" sx={{ color: '#4CAF50' }}>
              {currentQuestion} / {QUESTIONS}
            </Typography>
          </Box>
          
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              分數
            </Typography>
            <Typography variant="h6" sx={{ color: '#4CAF50' }}>
              {score}
            </Typography>
          </Box>
        </Box>
        
        <Box sx={{ textAlign: 'center', minWidth: 200 }}>
          <Typography variant="body1" sx={{ 
            color: showAnswer ? 
              (message.includes('正確') ? '#4CAF50' : '#F44336') : '#FFC107',
            fontWeight: 'bold',
            height: '24px',
          }}>
            {message}
          </Typography>
        </Box>
      </Box>

      {/* 主遊戲區域 */}
      <Box sx={{ 
        flex: 1, 
        display: 'flex', 
        p: 2, 
        gap: 2,
        minHeight: 0,
      }}>
        {/* 左側：題目區域 */}
        {isPlaying && renderQuestionArea()}

        {/* 右側：遊戲板區域 */}
        <Paper
  elevation={10}
  sx={{
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    p: 1, // 減少 padding 讓空間更大
    borderRadius: 2,
    border: '2px solid rgba(255, 255, 255, 0.2)',
    overflow: 'auto',
    minHeight: 0,
    width: '100%',
    height: '100%',
  }}
>
  <Box
    sx={{
      display: 'grid',
      gridTemplateColumns: `repeat(${COLS}, ${cellSize}px)`,
      gridTemplateRows: `repeat(${ROWS}, ${cellSize}px)`,
      gap: '1px', // 減少間距讓燈更密集
      justifyContent: 'center',
      alignContent: 'center',
      backgroundColor: 'rgba(30, 30, 30, 0.8)',
      borderRadius: 1,
      border: '1px solid rgba(255, 255, 255, 0.1)',
      boxShadow: 'inset 0 0 30px rgba(0, 0, 0, 0.5)',
      width: 'fit-content',
      height: 'fit-content',
      margin: 'auto',
    }}
  >
    {board.map((row, rowIndex) =>
      row.map((cell, colIndex) => renderCell(cell, rowIndex, colIndex))
    )}
  </Box>
</Paper>
      </Box>

      {/* 底部控制區域 */}
      <Box sx={{ 
        backgroundColor: 'rgba(0, 0, 0, 0.8)', 
        p: 2, 
        borderTop: '2px solid rgba(255, 255, 255, 0.1)',
      }}>
        <Stack direction="row" spacing={2} justifyContent="center">
          {!isPlaying ? (
            <Button
              variant="contained"
              color="primary"
              size="large"
              onClick={startNewGame}
              sx={{
                px: 6,
                py: 1.5,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              }}
            >
              開始遊戲
            </Button>
          ) : (
            <>
              <Button
                variant="contained"
                color="secondary"
                size="large"
                onClick={nextQuestion}
                disabled={!showAnswer || currentQuestion >= QUESTIONS}
                sx={{
                  px: 6,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  background: 'linear-gradient(45deg, #FF9800 30%, #FFB74D 90%)',
                  opacity: (!showAnswer || currentQuestion >= QUESTIONS) ? 0.5 : 1,
                }}
              >
                {currentQuestion >= QUESTIONS ? '完成所有題目' : '下一題'}
              </Button>
              <Button
                variant="outlined"
                color="error"
                size="large"
                onClick={() => {
                  setIsPlaying(false);
                  setMessage('遊戲已重置。');
                }}
                sx={{
                  px: 6,
                  py: 1.5,
                  fontSize: '1.1rem',
                  fontWeight: 'bold',
                  borderWidth: 2,
                  borderColor: '#F44336',
                  color: '#F44336',
                  '&:hover': {
                    borderWidth: 2,
                    backgroundColor: 'rgba(244, 67, 54, 0.1)',
                  }
                }}
              >
                重新開始
              </Button>
            </>
          )}
        </Stack>

        {/* 顏色說明 */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          gap: 3, 
          mt: 2,
          flexWrap: 'wrap',
        }}>
          {COLORS.map((color, index) => (
            <Box key={index} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box
                sx={{
                  width: 16,
                  height: 16,
                  backgroundColor: color,
                  borderRadius: '50%',
                  border: '1px solid rgba(255, 255, 255, 0.5)',
                }}
              />
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                {['綠色', '黃色', '紅色', '藍色'][index]}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* 遊戲結束對話框 */}
      <Dialog 
        open={gameOver} 
        onClose={handleGameOverClose}
        PaperProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.95)',
            color: 'white',
            border: '3px solid rgba(255, 255, 255, 0.2)',
            borderRadius: 2,
          }
        }}
      >
        <DialogTitle sx={{ textAlign: 'center', borderBottom: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#FFC107' }}>
            遊戲結束！
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ py: 4 }}>
          <Typography variant="h3" align="center" sx={{ my: 3, color: '#4CAF50' }}>
            {score} / {QUESTIONS}
          </Typography>
          
          <Typography variant="h5" align="center" sx={{ 
            mb: 3,
            color: score === QUESTIONS ? '#FFC107' :
                   score >= QUESTIONS * 0.8 ? '#4CAF50' :
                   score >= QUESTIONS * 0.6 ? '#2196F3' : '#F44336'
          }}>
            {score === QUESTIONS ? '🎉 完美！全對！記憶力超強！' :
             score >= QUESTIONS * 0.8 ? '🎯 表現非常出色！' :
             score >= QUESTIONS * 0.6 ? '👍 不錯的表現！' :
             '💪 繼續練習，下次會更好！'}
          </Typography>
          
          <Typography variant="h6" align="center" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
            正確率：{Math.round((score / QUESTIONS) * 100)}%
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 3, borderTop: '1px solid rgba(255, 255, 255, 0.1)' }}>
          <Button 
            onClick={handleGameOverClose} 
            variant="outlined"
            sx={{ 
              px: 4,
              borderColor: 'rgba(255, 255, 255, 0.3)',
              color: 'rgba(255, 255, 255, 0.8)',
              '&:hover': {
                borderColor: 'rgba(255, 255, 255, 0.5)',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              }
            }}
          >
            返回
          </Button>
          <Button 
            onClick={() => { handleGameOverClose(); startNewGame(); }} 
            variant="contained"
            sx={{ 
              px: 4,
              background: 'linear-gradient(45deg, #2196F3 30%, #21CBF3 90%)',
              '&:hover': {
                background: 'linear-gradient(45deg, #1976D2 30%, #1E88E5 90%)',
              }
            }}
          >
            再玩一次
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}